<template>
  <div :class="['podium-card', `place-${place}`]">
    <div class="card-header">
      <div class="community-info">
        <img :src="community.avatar" :alt="community.name" class="community-avatar" />
        <div class="community-details">
          <h3 class="community-name">{{ community.name }}</h3>
          <div class="place-text">
            {{ placeNumber }}° {{ placeLabel }}
          </div>
        </div>
      </div>
      <img :src="medalIcon" :alt="`${placeLabel} medal`" class="medal-icon" />
    </div>

    <div class="metrics">
      <div class="metric-item">
        <span class="metric-label">Volume</span>
        <span class="metric-value">{{ formatNumber(community.volume) }}</span>
      </div>
      <div v-if="community.points !== null && community.points !== undefined" class="metric-item">
        <span class="metric-label">Points</span>
        <span class="metric-value">{{ formatNumber(community.points) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeaguePodiumCard',
  props: {
    community: {
      type: Object,
      required: true,
      // Expected: { id, name, avatar, volume, points }
    },
    place: {
      type: Number,
      required: true,
      validator: (value) => [1, 2, 3].includes(value)
    }
  },
  computed: {
    placeNumber() {
      return this.place;
    },
    placeLabel() {
      const labels = {
        1: 'First Place',
        2: 'Second Place',
        3: 'Third Place'
      };
      return labels[this.place];
    },
    medalIcon() {
      const medals = {
        1: require('@/apps/broker/designs/leagues/gold-medal.png'),
        2: require('@/apps/broker/designs/leagues/silver-medal.png'),
        3: require('@/apps/broker/designs/leagues/bronze-medal.png')
      };
      return medals[this.place];
    }
  },
  methods: {
    formatNumber(num) {
      if (!num) return '0';
      return num.toLocaleString('en-US');
    }
  }
};
</script>

<style lang="scss" scoped>
.podium-card {
  border-radius: 16px;
  padding: 24px;
  min-width: 280px;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  &.place-1 {
    background: linear-gradient(180deg, #FFECBB 0%, #FFFAEF 100%);
    padding: 24px;
    min-height: 210px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60px;
      background: linear-gradient(180deg, rgba(255, 250, 239, 0) 0%, rgba(255, 236, 187, 0.15) 100%);
      border-radius: 0 0 16px 16px;
      pointer-events: none;
    }
  }

  &.place-2 {
    background: linear-gradient(180deg, #DEDEDE 0%, #FBFBFB 100%);
    padding: 24px;
    min-height: 159px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 50px;
      background: linear-gradient(180deg, rgba(251, 251, 251, 0) 0%, rgba(222, 222, 222, 0.12) 100%);
      border-radius: 0 0 16px 16px;
      pointer-events: none;
    }
  }

  &.place-3 {
    background: linear-gradient(180deg, #F2C380 0%, #FFF8EE 100%);
    padding: 24px;
    min-height: 159px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 50px;
      background: linear-gradient(180deg, rgba(255, 248, 238, 0) 0%, rgba(242, 195, 128, 0.12) 100%);
      border-radius: 0 0 16px 16px;
      pointer-events: none;
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-right: 64px; // Make space for absolute positioned medal
}

.community-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex: 1;
}

.community-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #FFFFFF;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.community-details {
  flex: 1;
}

.community-name {
  font-family: 'Avenir', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1A1A1A;
  margin: 0 0 4px 0;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.place-text {
  font-family: 'Avenir', sans-serif;
  font-size: 12px;
  margin: 0;
  display: inline-block;
  background: #FFFFFF;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 700;
}

.place-1 .place-text {
  color: #FFC123;
}

.place-2 .place-text {
  color: #ABABAB;
}

.place-3 .place-text {
  color: #D38851;
}

.medal-icon {
  position: absolute;
  top: 0;
  right: -6px;
  width: 80px;
  height: 80px;
  object-fit: contain;
  flex-shrink: 0;
}

.metrics {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.metric-label {
  font-family: 'Avenir', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1A1A1A;
}

.metric-value {
  font-family: 'Avenir', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #333333;
}

.theme--dark {
  .podium-card {
    &.place-1 {
      background: linear-gradient(135deg, #4A3F1F 0%, #6B5A2D 100%);
    }

    &.place-2 {
      background: linear-gradient(135deg, #2A2A2A 0%, #3A3A3A 100%);
    }

    &.place-3 {
      background: linear-gradient(135deg, #3F2F1F 0%, #5A4530 100%);
    }
  }

  .community-name,
  .metric-label {
    color: #FFFFFF;
  }

  .metric-value {
    color: #CCCCCC;
  }

  .place-text {
    background: rgba(255, 255, 255, 0.15);
  }

  .place-1 .place-text {
    color: #FFC123;
  }

  .place-2 .place-text {
    color: #ABABAB;
  }

  .place-3 .place-text {
    color: #D38851;
  }
}
</style>
